// Athletics Scoring (117) -- per-discipline competitions with ranked entries.
// No team panels; a field of competitors.

import { useCallback, useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import type { AthleticsScoreResponse, AthleticsCompetitionPublic } from "@dugads/shared";
import * as api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ChevronLeft, Plus, Trash2, Loader2 } from "lucide-react";

export default function AthleticsMatchScorePage() {
  const { id } = useParams<{ id: string }>();
  const [data, setData] = useState<AthleticsScoreResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [discipline, setDiscipline] = useState("");
  const [unit, setUnit] = useState<"seconds" | "metres" | "kilograms">("seconds");

  const load = useCallback(() => {
    if (!id) return;
    api.getAthleticsScore(id).then(setData)
      .catch((e) => setError(e instanceof api.ApiError ? e.message : "Failed to load"))
      .finally(() => setLoading(false));
  }, [id]);
  useEffect(() => load(), [load]);

  async function addDiscipline() {
    if (!id || !discipline.trim()) return;
    setData(await api.createAthleticsCompetition(id, { discipline: discipline.trim(), unit }));
    setDiscipline("");
  }

  if (loading) return <div className="h-40 animate-pulse rounded-lg bg-muted" data-page="athletics-score" data-state="loading" />;
  if (error || !data) return <p className="text-sm text-destructive" data-page="athletics-score" data-error>{error ?? "Not found"}</p>;

  const canManage = data._actions.can_manage;

  return (
    <div className="mx-auto flex max-w-2xl flex-col gap-5" data-page="athletics-score" data-state="ready">
      <Link to={`/events/${data.event_id}`} className="inline-flex w-fit items-center gap-1 text-sm text-muted-foreground hover:text-foreground">
        <ChevronLeft className="h-3.5 w-3.5" /> Back to event
      </Link>
      <h1 className="text-xl font-semibold">{data.match_name}</h1>

      {canManage && (
        <div className="flex flex-wrap items-end gap-2 rounded-lg border border-border p-3">
          <label className="flex flex-col gap-1 text-xs">
            <span className="font-medium">Discipline</span>
            <input value={discipline} onChange={(e) => setDiscipline(e.target.value)} placeholder="100m, long jump..." className="rounded-md border border-input bg-background px-3 py-1.5 text-sm" data-field="discipline" />
          </label>
          <label className="flex flex-col gap-1 text-xs">
            <span className="font-medium">Unit</span>
            <select value={unit} onChange={(e) => setUnit(e.target.value as typeof unit)} className="rounded-md border border-input bg-background px-3 py-1.5 text-sm" data-field="unit">
              <option value="seconds">seconds (track)</option>
              <option value="metres">metres (field)</option>
              <option value="kilograms">kilograms (field)</option>
            </select>
          </label>
          <Button size="sm" onClick={addDiscipline} disabled={!discipline.trim()} data-action="add-discipline"><Plus className="mr-1 h-4 w-4" /> Add discipline</Button>
        </div>
      )}

      {data.competitions.length === 0 ? (
        <p className="rounded-lg border border-border bg-muted/30 px-6 py-8 text-center text-sm text-muted-foreground">No disciplines yet.</p>
      ) : (
        data.competitions.map((c) => <Competition key={c.id} comp={c} canManage={canManage} onChange={setData} />)
      )}
    </div>
  );
}

function Competition({ comp, canManage, onChange }: { comp: AthleticsCompetitionPublic; canManage: boolean; onChange: (d: AthleticsScoreResponse) => void }) {
  const [name, setName] = useState("");
  const [busy, setBusy] = useState(false);

  async function addEntry() {
    if (!name.trim()) return;
    setBusy(true);
    try { onChange(await api.addAthleticsEntry(comp.id, { athlete_name: name.trim() })); setName(""); }
    finally { setBusy(false); }
  }
  async function setResult(entryId: string, value: string) {
    const num = value.trim() === "" ? null : Number(value);
    onChange(await api.updateAthleticsEntry(entryId, { result_value: Number.isNaN(num as number) ? null : num, result_display: value.trim() || null }));
  }
  async function toggleDq(entryId: string, dq: boolean) {
    onChange(await api.updateAthleticsEntry(entryId, { dq }));
  }
  async function removeEntry(entryId: string) {
    onChange(await api.deleteAthleticsEntry(entryId));
  }
  async function removeDiscipline() {
    if (!confirm(`Remove ${comp.discipline}?`)) return;
    onChange(await api.deleteAthleticsCompetition(comp.id));
  }

  return (
    <div className="rounded-lg border border-border" data-competition={comp.id}>
      <div className="flex items-center justify-between border-b border-border px-4 py-2">
        <h3 className="text-sm font-semibold">{comp.discipline} <span className="text-xs font-normal text-muted-foreground">({comp.unit})</span></h3>
        {canManage && <button type="button" onClick={removeDiscipline} className="text-muted-foreground hover:text-destructive" aria-label="Remove discipline"><Trash2 className="h-4 w-4" /></button>}
      </div>
      <table className="w-full text-sm">
        <thead className="text-left text-xs uppercase text-muted-foreground">
          <tr><th className="px-4 py-1.5 w-10">#</th><th className="px-2 py-1.5">Athlete</th><th className="px-2 py-1.5">Result</th>{canManage && <th className="px-2 py-1.5"><span className="sr-only">Actions</span></th>}</tr>
        </thead>
        <tbody className="divide-y divide-border/60">
          {comp.entries.map((e) => (
            <tr key={e.id} data-entry={e.id} className={e.dq ? "opacity-60" : ""}>
              <td className="px-4 py-1.5 font-semibold tabular-nums">{e.dq ? "DQ" : e.placing ?? "-"}</td>
              <td className="px-2 py-1.5">{e.athlete_name}{e.school_name && <span className="ml-1 text-xs text-muted-foreground">{e.school_name}</span>}{e.pb && <span className="ml-1 rounded bg-[#E1F5EE] px-1 text-[10px] text-[#085041]">PB</span>}</td>
              <td className="px-2 py-1.5">
                {canManage ? (
                  <input defaultValue={e.result_display ?? ""} onBlur={(ev) => setResult(e.id, ev.target.value)} placeholder="--" className="w-20 rounded border border-input bg-background px-2 py-0.5 text-sm" data-field="result" />
                ) : (e.result_display ?? "-")}
              </td>
              {canManage && (
                <td className="px-2 py-1.5 text-right">
                  <button type="button" onClick={() => toggleDq(e.id, !e.dq)} className="mr-2 text-xs text-amber-700 hover:underline" data-action="toggle-dq">{e.dq ? "un-DQ" : "DQ"}</button>
                  <button type="button" onClick={() => removeEntry(e.id)} className="text-xs text-destructive hover:underline" data-action="remove-entry">remove</button>
                </td>
              )}
            </tr>
          ))}
          {comp.entries.length === 0 && <tr><td colSpan={canManage ? 4 : 3} className="px-4 py-2 text-xs text-muted-foreground">No entries.</td></tr>}
        </tbody>
      </table>
      {canManage && (
        <div className="flex gap-2 border-t border-border px-4 py-2">
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="Add athlete..." className="flex-1 rounded-md border border-input bg-background px-3 py-1.5 text-sm" data-field="athlete-name" />
          <Button size="sm" variant="outline" onClick={addEntry} disabled={busy || !name.trim()} data-action="add-entry">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
          </Button>
        </div>
      )}
    </div>
  );
}
